/*
 * Managing product tags and category tags.
 *
 * These two catalogues are the whole of the metadata an article points at, which
 * is why MetadataKind has exactly two members and why this page has exactly two
 * tabs. A tab added here without a member added there would not compile.
 *
 * Every list offers "retire" alongside "delete", and says which one you probably
 * want. That is not hedging: IsActive exists on both tables precisely because
 * removing a tag from the editor is a different operation from destroying the
 * assignments and text that depend on it, and the destructive one is almost never
 * what someone reaching for "remove this tag" actually means. Delete is still there,
 * and still goes through the impact dialog.
 *
 * Release notes carry a product too (S42, 2026-10-12) - the same catalogue the
 * owner asked them to "reference" - so the product table counts them in a column
 * of their own, from the admin-only Product.releaseNoteCount. A product any
 * release note uses cannot be deleted (FK without cascade, and a blocker in the
 * impact report), which is one more reason the Delete tooltip points at Retire.
 *
 * A category tag may relate to products (S52, 2026-11-05): the category table has
 * a Product column - their names, blank for none - and the category dialog a
 * multi-select dropdown of every product tag with a blank first option, which is
 * checked when nothing is and clears the rest when chosen (ProductMultiSelect).
 * The owner: "Initially this Category tag property will have no effect" - it is
 * for a later "choose a product, see its categories".
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48).
 */

import { useEffect, useRef, useState } from 'react';
import type { Category, DeleteResult, MetadataKind, Product } from '@accotool-kb/shared';
import { isValidCategoryName, isValidSlug, toSlug } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Field, Loading, Modal } from '../components/ui.js';
import type { siteEn } from '../i18n/admin/site.en.js';
import { useMessages } from '../i18n/language.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';

/**
 * One tab per catalogue, keyed by the shared MetadataKind so that a tab and the
 * delete target it opens name the same thing. A third catalogue would have to be
 * added to MetadataKind first, which is what stops this page drifting from the
 * impact machinery that consumes those names.
 */
type TabKey = MetadataKind;

const TABS: TabKey[] = ['product', 'category'];

export function MetadataPage(): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.metadata;
  const api = useAdminApi();
  const [tab, setTab] = useState<TabKey>('product');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(() => api.metadata(), []);

  const onDeleted = (result: DeleteResult) => {
    setNotice(
      s.deleted(result.targetLabel, result.performed.map((step) => step.description).join(' ')),
    );
    state.reload();
  };

  if (state.loading && !state.data) return <Loading label={s.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p className="muted">{s.intro}</p>
        </div>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <div className="tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {s[key].tab}
          </button>
        ))}
      </div>

      {tab === 'product' && (
        <ProductsTab
          products={state.data.products}
          onChanged={state.reload}
          onDelete={setDeleteTarget}
        />
      )}
      {tab === 'category' && (
        <CategoriesTab
          categories={state.data.categories}
          products={state.data.products}
          onChanged={state.reload}
          onDelete={setDeleteTarget}
        />
      )}
      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={onDeleted}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Product and category tags - the same shape, so one dialog serves both
// ---------------------------------------------------------------------------

function TagTable({
  kind,
  rows,
  onEdit,
  onNew,
  onDelete,
}: {
  kind: 'product' | 'category';
  rows: Array<{
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
    articleCount: number;
    /** Products only: release notes carrying it, in every state. */
    releaseNoteCount?: number;
    /** Categories only (S52): the names of the products it relates to, none for none. */
    productNames?: string[];
  }>;
  onEdit: (id: number) => void;
  onNew: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const s = useMessages().admin.site.metadata;
  // Only a product can carry a release note, so only its table has the column.
  const showReleaseNotes = kind === 'product';
  // Only a category relates to a product (S52), so only its table has that one.
  const showProduct = kind === 'category';
  const columnCount = 5 + (showReleaseNotes ? 1 : 0) + (showProduct ? 1 : 0);

  return (
    <>
      <div className="card-header">
        <p className="muted" style={{ margin: 0 }}>
          {s[kind].intro}
        </p>
        <button type="button" className="button button-primary" onClick={onNew}>
          {s[kind].newTag}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{s.name}</th>
                <th scope="col">{s.slug}</th>
                {showProduct && <th scope="col">{s.productColumn}</th>}
                <th scope="col">{s.articles}</th>
                {showReleaseNotes && <th scope="col">{s.releaseNotes}</th>}
                <th scope="col">{s.state}</th>
                <th scope="col">
                  <span className="visually-hidden">{s.actions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  {/* Blank for none, as the dropdown's own first option is. */}
                  {showProduct && <td>{(row.productNames ?? []).join(', ')}</td>}
                  <td>{row.articleCount}</td>
                  {showReleaseNotes && <td>{row.releaseNoteCount ?? 0}</td>}
                  <td>
                    {row.isActive ? (
                      <span className="badge badge-live">{s.active}</span>
                    ) : (
                      <span className="badge badge-hidden">{s.retired}</span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => onEdit(row.id)}
                    >
                      {s.edit}
                    </button>{' '}
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() =>
                        onDelete({ kind, targetId: String(row.id), label: row.name })
                      }
                      title={describeUse(row.articleCount, row.releaseNoteCount ?? 0, s)}
                    >
                      {s.delete}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="muted">
                    {s[kind].none}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * The Delete button's tooltip: what uses the tag, when anything does. The
 * article-only sentence is unchanged; a product used by release notes says so
 * too, because those make the delete impossible rather than merely costly - a
 * product a release note carries is a blocker in the impact report.
 */
function describeUse(
  articleCount: number,
  releaseNoteCount: number,
  s: (typeof siteEn)['metadata'],
): string | undefined {
  if (articleCount === 0 && releaseNoteCount === 0) return undefined;
  if (releaseNoteCount === 0) return s.usedByArticles(articleCount);
  return s.usedByReleaseNotes(articleCount, releaseNoteCount);
}

function ProductsTab({
  products,
  onChanged,
  onDelete,
}: {
  products: Product[];
  onChanged: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const api = useAdminApi();
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const current = editing?.id ? products.find((row) => row.productId === editing.id) : undefined;

  return (
    <>
      <TagTable
        kind="product"
        rows={products.map((product) => ({
          id: product.productId,
          name: product.name,
          slug: product.slug,
          isActive: product.isActive,
          articleCount: product.articleCount ?? 0,
          releaseNoteCount: product.releaseNoteCount ?? 0,
        }))}
        onEdit={(id) => setEditing({ id })}
        onNew={() => setEditing({ id: null })}
        onDelete={onDelete}
      />

      {editing && (
        <TagDialog
          kind="product"
          initial={
            current
              ? { name: current.name, slug: current.slug, isActive: current.isActive }
              : null
          }
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            editing.id ? api.updateProduct(editing.id, input) : api.createProduct(input)
          }
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function CategoriesTab({
  categories,
  products,
  onChanged,
  onDelete,
}: {
  categories: Category[];
  /** Every product tag, retired ones included: what a category may relate to (S52). */
  products: Product[];
  onChanged: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const api = useAdminApi();
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const current = editing?.id
    ? categories.find((row) => row.categoryId === editing.id)
    : undefined;
  // In the products' own order - by name, as the API lists them - not the ids'.
  const namesOf = (productIds: readonly number[] = []) =>
    products.filter((product) => productIds.includes(product.productId)).map((product) => product.name);

  return (
    <>
      <TagTable
        kind="category"
        rows={categories.map((category) => ({
          id: category.categoryId,
          name: category.name,
          slug: category.slug,
          isActive: category.isActive,
          articleCount: category.articleCount ?? 0,
          productNames: namesOf(category.productIds),
        }))}
        onEdit={(id) => setEditing({ id })}
        onNew={() => setEditing({ id: null })}
        onDelete={onDelete}
      />

      {editing && (
        <TagDialog
          kind="category"
          products={products}
          initial={
            current
              ? {
                  name: current.name,
                  slug: current.slug,
                  isActive: current.isActive,
                  productIds: current.productIds ?? [],
                }
              : null
          }
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            editing.id ? api.updateCategory(editing.id, input) : api.createCategory(input)
          }
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

/** What the dialog saves. `productIds` is the category's alone (S52), and always sent for one. */
interface TagDialogInput {
  name: string;
  slug: string;
  isActive: boolean;
  productIds?: number[];
}

function TagDialog({
  kind,
  products,
  initial,
  onClose,
  onSubmit,
  onSaved,
}: {
  kind: 'product' | 'category';
  /** A category's dialog only: the products the dropdown offers. */
  products?: Product[];
  initial: { name: string; slug: string; isActive: boolean; productIds?: number[] } | null;
  onClose: () => void;
  onSubmit: (input: TagDialogInput) => Promise<unknown>;
  onSaved: () => void;
}): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.metadata;
  const action = useAction();
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(initial));
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  // A category's products (S52): none - the blank option - unless the category
  // being edited already relates to some.
  const [productIds, setProductIds] = useState<number[]>(initial?.productIds ?? []);

  const effectiveSlug = slugTouched ? slug : toSlug(name);
  // The comma rule is the category's alone: its names are listed comma-separated
  // on an article and split again on the way to the reader, a product's never
  // are. Said here, before the request, in the words the schema would answer with.
  const nameError =
    name.trim().length === 0
      ? s.nameRequired
      : kind === 'category' && !isValidCategoryName(name)
        ? t.admin.rules.categoryNameRequirement
        : undefined;
  const slugError = !isValidSlug(effectiveSlug)
    ? t.admin.rules.slugRequirement
    : undefined;

  const submit = async () => {
    if (nameError || slugError) return;
    const result = await action.run(() =>
      onSubmit({
        name: name.trim(),
        slug: effectiveSlug,
        isActive,
        // Always sent for a category - an empty list for none, so choosing the
        // blank option clears every relation.
        ...(kind === 'category' ? { productIds } : {}),
      }),
    );
    if (result !== undefined) onSaved();
  };

  return (
    <Modal
      open
      title={initial ? s[kind].editTag(initial.name) : s[kind].newTag}
      onClose={onClose}
      busy={action.running}
      footer={
        <>
          <button type="button" className="button" onClick={onClose} disabled={action.running}>
            {s.cancel}
          </button>
          <button
            type="button"
            className="button button-primary"
            onClick={() => void submit()}
            disabled={action.running || Boolean(nameError || slugError)}
          >
            {action.running ? s.saving : s.save}
          </button>
        </>
      }
    >
      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}

      <Field
        label={s.name}
        htmlFor="tag-name"
        required
        error={nameError}
        hint={kind === 'category' ? s.categoryNameHint : undefined}
      >
        <input
          id="tag-name"
          type="text"
          value={name}
          maxLength={200}
          /* data-autofocus, not autoFocus - see the comment in Modal. */
          data-autofocus
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      {/*
        A product slug is no longer only a query parameter: it is the first
        segment of every article address under it, /kb/<product>/<article>, so
        editing one moves every article the product carries. That is a different
        order of consequence from a filter link going stale, and the two kinds of
        tag have to say so differently.
      */}
      <Field
        label={s.slug}
        htmlFor="tag-slug"
        required
        error={slugError}
        hint={s[kind].slugHint}
      >
        <input
          id="tag-slug"
          type="text"
          value={effectiveSlug}
          maxLength={200}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          onBlur={(event) => setSlug(toSlug(event.target.value))}
        />
      </Field>

      {/*
        A category tag's products (the owner, 2026-11-05): first a dropdown with
        every product tag and a blank for no relation, the blank by default; then,
        the same day, "a multi-select dropdown list, so categories can be used for
        multiple products", where "Selecting the blank option, should clear other
        selections". Retired products are offered too, marked. Nothing reads it yet.
      */}
      {kind === 'category' && products && (
        <ProductMultiSelect products={products} value={productIds} onChange={setProductIds} />
      )}

      <div className="field">
        <label htmlFor="tag-active">{s.state}</label>
        <label className="inline-checkbox">
          <input
            id="tag-active"
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          <span>{s.active}</span>
        </label>
        <p className="field-hint">{s[kind].retireHint}</p>
      </div>
    </Modal>
  );
}

/**
 * The category dialog's products (S52): a multi-select dropdown - a button that
 * says what is chosen, opening a list of checkboxes under it.
 *
 * Checkboxes, not a native <select multiple>: that is a list box that wants
 * Ctrl-click, not a dropdown. The list opens in the flow of the dialog rather
 * than floating over it, so the dialog's scrolling body can never clip it.
 *
 * The blank comes first, checked exactly when nothing is chosen, as the single
 * dropdown's blank was selected by default; checking it clears every product
 * (the owner's "Selecting the blank option, should clear other selections"),
 * and checking a product unchecks it. It reads "No product" to a screen reader
 * and shows nothing, as the owner's blank did.
 *
 * Escape closes the list and leaves the dialog open - the keydown is handled
 * here, before the dialog's own Escape - and so does a click outside it.
 */
function ProductMultiSelect({
  products,
  value,
  onChange,
}: {
  products: Product[];
  value: number[];
  onChange: (next: number[]) => void;
}): JSX.Element {
  const s = useMessages().admin.site.metadata;
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  // In the products' order - by name - whatever order they were ticked in.
  const summary = products
    .filter((product) => value.includes(product.productId))
    .map((product) => product.name)
    .join(', ');

  return (
    <div className="field">
      <label id="tag-products-label" htmlFor="tag-products">
        {s.productColumn}
      </label>
      <div
        ref={container}
        className="multi-select"
        onKeyDown={(event) => {
          if (open && event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            button.current?.focus();
          }
        }}
      >
        <button
          ref={button}
          id="tag-products"
          type="button"
          className="multi-select-button"
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={open ? 'tag-products-list' : undefined}
          aria-labelledby="tag-products-label tag-products-summary"
          aria-describedby="tag-products-hint"
          onClick={() => setOpen((current) => !current)}
        >
          <span id="tag-products-summary" className="multi-select-summary">
            {summary}
          </span>
          <span className="multi-select-caret" aria-hidden="true">
            ▾
          </span>
        </button>
        {open && (
          <div id="tag-products-list" className="multi-select-list" role="group" aria-labelledby="tag-products-label">
            <label className="multi-select-option">
              <input
                type="checkbox"
                checked={value.length === 0}
                aria-label={s.noProduct}
                onChange={() => onChange([])}
              />
              <span className="multi-select-blank" aria-hidden="true" />
            </label>
            {products.map((product) => (
              <label key={product.productId} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={value.includes(product.productId)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...value, product.productId]
                        : value.filter((productId) => productId !== product.productId),
                    )
                  }
                />
                <span>{product.isActive ? product.name : s.retiredProduct(product.name)}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <p id="tag-products-hint" className="field-hint">
        {s.categoryProductHint}
      </p>
    </div>
  );
}
